import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import { Button, Container, Paper, Typography } from '@mui/material';

import useInsuranceInfo from '../../hooks/useInsuranceInfo';
import { DECLINED, PENDING } from '../../shared/contractStatus';
import {
  GET_CUSTOMER,
  INSURANCE_TYPE_DENTOLO,
  INSURANCE_TYPE_PETOLO,
  INSURANCE_TYPE_VITOLO,
  SIGN_OUT,
  useReady,
} from '../../shared';

import ButtonContainer from '../../components/ButtonContainer';
import IllustrationDesktopDentolo from './IllustrationDesktopDentolo';
import IllustrationDesktopPetolo from './IllustrationDesktopPetolo';
import IllustrationMobileDentolo from './IllustrationMobileDentolo';
import IllustrationMobilePetolo from './IllustrationMobilePetolo';
import IllustrationDesktopVitolo from './IllustrationDesktopVitolo';
import IllustrationMobileVitolo from './IllustrationMobileVitolo';
import { PageTitle } from '../../components/common';

import {
  StyledIllustrationContainerMobile,
  StyledIllustrationContainerDesktop,
  StyledContractPendingApprovalContent,
} from './ContractPendingApproval.styled';

const ContractNotApprovedPageTitle = ({ insuranceType }) => {
  switch (insuranceType) {
    case INSURANCE_TYPE_DENTOLO:
      return <FormattedMessage id="contract_not_approved.title.dentolo" />;
    case INSURANCE_TYPE_PETOLO:
      return <FormattedMessage id="contract_not_approved.title.petolo" />;
    case INSURANCE_TYPE_VITOLO:
      return <FormattedMessage id="contract_not_approved.title.vitolo" />;
    default:
      return <FormattedMessage id="contract_not_approved.title.dentolo" />;
  }
};

const DesktopIllustration = ({ insuranceType }) => {
  switch (insuranceType) {
    case INSURANCE_TYPE_DENTOLO:
      return <IllustrationDesktopDentolo />;
    case INSURANCE_TYPE_PETOLO:
      return <IllustrationDesktopPetolo />;
    case INSURANCE_TYPE_VITOLO:
      return <IllustrationDesktopVitolo />;
    default:
      return <IllustrationDesktopDentolo />;
  }
};

const MobileIllustration = ({ insuranceType }) => {
  switch (insuranceType) {
    case INSURANCE_TYPE_DENTOLO:
      return <IllustrationMobileDentolo />;
    case INSURANCE_TYPE_PETOLO:
      return <IllustrationMobilePetolo />;
    case INSURANCE_TYPE_VITOLO:
      return <IllustrationMobileVitolo />;
    default:
      return <IllustrationMobileDentolo />;
  }
};

const contentOnPaper = (insuranceType) => {
  switch (insuranceType) {
    case INSURANCE_TYPE_DENTOLO:
    case INSURANCE_TYPE_PETOLO:
      return true;
    case INSURANCE_TYPE_VITOLO:
      return false;
    default:
      return true;
  }
};

export default function ContractPendingApproval() {
  const history = useHistory();
  const ready = useReady();

  const { type: insuranceType } = useInsuranceInfo();

  const { loading: customerLoading, data: customerData } = useQuery(GET_CUSTOMER);
  const contractStatus = customerData?.customer?.contract?.status;

  const [signOut] = useMutation(SIGN_OUT, {
    refetchQueries: [{ query: GET_CUSTOMER }],
  });

  useEffect(() => {
    if (!customerLoading) {
      if (contractStatus === DECLINED) {
        signOut().then(() => history.push('/login?invalid=true'));
      } else if (contractStatus !== PENDING) {
        history.push('/');
      }
    }
  }, [history, signOut, contractStatus, customerLoading]);

  return (
    <CSSTransition
      in={ready && !customerLoading}
      timeout={600}
      classNames="slow-fade"
      unmountOnExit
    >
      <Container>
        <PageTitle>
          <Typography variant="h1">
            <ContractNotApprovedPageTitle insuranceType={insuranceType} />
          </Typography>
        </PageTitle>

        <StyledIllustrationContainerDesktop>
          <DesktopIllustration insuranceType={insuranceType} />
        </StyledIllustrationContainerDesktop>

        <StyledIllustrationContainerMobile>
          <MobileIllustration insuranceType={insuranceType} />
        </StyledIllustrationContainerMobile>

        <Paper elevation={contentOnPaper(insuranceType) ? 1 : 0}>
          <StyledContractPendingApprovalContent>
            <Typography variant="p">
              <FormattedMessage id="contract_not_approved.text_1" />
            </Typography>
          </StyledContractPendingApprovalContent>

          <StyledContractPendingApprovalContent>
            <Typography variant="p">
              <FormattedMessage id="contract_not_approved.text_2" />
            </Typography>
          </StyledContractPendingApprovalContent>

          <ButtonContainer>
            <Button type="submit" onClick={() => history.push('/')}>
              <Typography variant="buttons">
                <FormattedMessage id="contract_not_approved.button" />
              </Typography>
            </Button>
          </ButtonContainer>
        </Paper>
      </Container>
    </CSSTransition>
  );
}
